// ─── Help Content for all pages ───────────────────────────────────────────────
// Each page has: functionality, userGuide, faq, and tourSteps
// tourSteps map to data-help-tour="<id>" attributes placed on DOM elements.

export interface HelpFeature {
  title: string;
  description: string;
}

export interface UserGuideStep {
  step: number;
  text: string;
}

export interface UserGuideSection {
  title: string;
  intro?: string;
  steps: UserGuideStep[];
  note?: string;
  noteType?: 'info' | 'warning';
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface HelpTourStep {
  id: string;
  title: string;
  description: string;
  targetSelector?: string;
  placement: 'top' | 'bottom' | 'left' | 'right' | 'center';
  icon: string;
}

export interface RelatedPage {
  label: string;
  page: string;
}

export interface PageHelpContent {
  pageTitle: string;
  pageDescription: string;
  functionality: {
    overview: string;
    features: HelpFeature[];
    sectionsAndFields?: string;
  };
  userGuide: {
    intro?: string;
    sections: UserGuideSection[];
  };
  faq: FAQItem[];
  tourSteps: HelpTourStep[];
  relatedPages?: RelatedPage[];
}

export type HelpPageKey =
  | 'calendar'
  | 'heatmap'
  | 'resources'
  | 'projects'
  | 'settings'
  | 'open-demand-requests'
  | 'open-demand-unallocated'
  | 'overview'
  | 'report-utilisation'
  | 'report-utilisation-forecast'
  | 'report-available-capacity'
  | 'report-resource-allocation'
  | 'report-project-staffing'
  | 'report-bench'
  | 'roles-permissions'
  | 'user-setup';

export const HELP_CONTENT: Record<HelpPageKey, PageHelpContent> = {

  // ─── Calendar / Planner ─────────────────────────────────────────────────────
  calendar: {
    pageTitle: 'Planner',
    pageDescription: 'Create, edit, and manage resource bookings across your team on a visual timeline.',
    functionality: {
      overview:
        'The Planner is your central workspace for scheduling resources across projects and time. There are multiple ways to create, edit, and manage bookings — from clicking the header button to right-clicking directly on the timeline. The resource list can be grouped and filtered in several ways to give you exactly the view you need. The planner updates in real time and reflects current utilisation across your team.',
      features: [
        {
          title: 'Booking Creation',
          description: 'Create bookings directly from the timeline or via the toolbar. Click or drag on any empty cell to pre-fill resource and dates, use the "Add Booking" button in the header to start from scratch, or right-click an empty cell for quick-add and paste options.',
        },
        {
          title: 'Booking Editing',
          description: 'Click any booking block to open the full booking form. Right-click a booking to access additional options: Edit, Duplicate, Copy, Change Status, and Delete — all without opening the form.',
        },
        {
          title: 'Drag to Move & Resize',
          description: 'Drag a booking left or right to shift its dates, drag it to a different resource row to reassign it, or drag either edge to extend or shorten the duration. Utilisation updates automatically.',
        },
        {
          title: 'Resource Grouping',
          description: 'Reorganise the timeline rows by Customer, Project, Resource, or Role using the grouping dropdown. Each mode gives a different perspective on how work is distributed across the team.',
        },
        {
          title: 'Booking Form',
          description: 'The full booking form covers Customer, Project, Resource(s), Booking Type, Start and End Dates, Notes, custom fields, and recurrence. Multiple resources can be assigned in a single booking.',
        },
        {
          title: 'Filters & Search',
          description: 'Narrow the timeline by department, resource, role, project, cost centre, booking type, or booking status using the filter drawer. A search bar lets you highlight specific bookings across the full grid.',
        },
        {
          title: 'View Controls',
          description: 'Switch between Day, Week, Month, Quarter, and Year views. Use the date navigator to move the visible window and zoom controls to adjust the timeline scale.',
        },
        {
          title: 'Conflict Indicators',
          description: 'Overloaded resources are flagged automatically on the timeline. Switch to Heatmap view for a colour-coded capacity overview, and use the What-if Analysis: Burnout Resolution panel to review and act on overallocations.',
        },
      ],
      sectionsAndFields:
        'The planner grid shows resources as rows and dates as columns. The grouping dropdown sits in the top-left of the resource column header and controls how rows are organised (Customer, Project, Resource, Role). The header toolbar contains the date navigator, Add Booking button, zoom controls, view selector, and filter trigger.',
    },
    userGuide: {
      intro: 'Use the Planner to build and manage your team\'s schedule. Follow the steps below to get started with the most common workflows.',
      sections: [
        {
          title: 'How do I create a booking?',
          steps: [
            { step: 1, text: 'Choose how to start: click the "Add Booking" button in the toolbar, click once on an empty timeline cell, click and drag across multiple days, or right-click an empty cell and choose "Add Booking" or "Quick Add Booking".' },
            { step: 2, text: 'The booking form opens. Select the Booking Type to categorise the work (e.g. Billable, Internal, Leave).' },
            { step: 3, text: 'Set the Status — the four options are Planned, Confirmed, Cancelled, and Completed. The default status can be configured in your user settings.' },
            { step: 4, text: 'In the Schedule section, choose a duration: Full Day (00:00–23:59), Morning (00:00–11:59), Afternoon (12:00–23:59), or Hourly for a custom time range. Then confirm the Start and End dates — these will be pre-filled if you clicked or dragged on the timeline. To repeat the booking, use the Repeat option within the Schedule section.' },
            { step: 5, text: 'Select the Project using the searchable dropdown. If the project has a customer assigned, the Customer field will populate automatically.' },
            { step: 6, text: 'Select the Resource(s) to assign. Multiple resources can be added to the same booking.' },
            { step: 7, text: 'Add any Notes or complete any required custom fields.' },
            { step: 8, text: 'Click Save. The booking appears on the timeline immediately and utilisation updates automatically.' },
          ],
          note: 'If a project does not appear in the search, it may be inactive or you may not have permission to book against it. Contact your administrator.',
          noteType: 'warning',
        },
        {
          title: 'How do I edit an existing booking?',
          steps: [
            { step: 1, text: 'Click on the booking block in the timeline to open the full booking form, pre-populated with all current values.' },
            { step: 2, text: 'Alternatively, right-click the booking and choose "Edit Booking" from the context menu.' },
            { step: 3, text: 'Make your changes across any of the form tabs and click Save. The timeline updates instantly.' },
          ],
        },
        {
          title: 'How do I use the right-click context menu on a booking?',
          steps: [
            { step: 1, text: 'Right-click any booking block in the timeline to open the context menu.' },
            { step: 2, text: '"Edit Booking" — opens the full booking form pre-populated with all current values.' },
            { step: 3, text: '"Copy Booking" — copies the booking to your clipboard. Right-click an empty cell to paste it in a new position.' },
            { step: 4, text: '"Move" — allows you to move the booking to a different date or resource directly from the menu.' },
            { step: 5, text: '"Change Status" — opens a submenu to switch between Planned, Confirmed, Cancelled, and Completed without opening the full form.' },
            { step: 6, text: '"Delete Booking" — permanently removes the booking.' },
          ],
        },
        {
          title: 'How do I move or reschedule a booking?',
          steps: [
            { step: 1, text: 'Click the booking to open the form, update the Start and End dates in the Schedule section, and click Save.' },
            { step: 2, text: 'Alternatively, right-click the booking and choose "Edit Booking" to open the form and adjust the dates or resource.' },
            { step: 3, text: 'To drag, hover over the booking block — the cursor changes to indicate dragging is available. Drag left or right to shift dates, or drag to a different resource row to reassign.' },
            { step: 4, text: 'To resize by dragging, grab the left or right edge of the booking block and drag to extend or shorten the duration.' },
            { step: 5, text: 'Release to drop. If the target resource is overloaded a conflict indicator will appear.' },
          ],
          note: 'Drag and drop can be enabled or disabled in your user preferences. If dragging is not working, check Settings → User Preferences.',
          noteType: 'info',
        },
        {
          title: 'How do I change the resource grouping view?',
          steps: [
            { step: 1, text: 'Locate the grouping dropdown in the top-left corner of the resource column header (above the resource list).' },
            { step: 2, text: 'Click the dropdown and choose from: Customer (groups by client), Project (groups by project then resource), Resource (flat list), or Role (groups by assigned role).' },
            { step: 3, text: 'The timeline immediately reorganises to reflect the selected grouping. All bookings remain unchanged.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'What are all the ways I can create a booking?',
        answer: 'There are four ways: (1) Click the "Add Booking" button in the toolbar header. (2) Single-click any empty cell on the timeline — the resource and date are pre-filled. (3) Click and drag across multiple days on an empty row — the date range is pre-filled. (4) Right-click an empty cell and choose "Add Booking" for the full form, or "Quick Add Booking" for a fast-entry modal.',
      },
      {
        question: 'Can I book the same resource on multiple projects at the same time?',
        answer: 'Yes. Overlapping bookings are allowed and tracked. When total daily allocation exceeds 100%, the resource row is highlighted and flagged in the utilisation heatmap.',
      },
      {
        question: 'How do I set up a recurring booking?',
        answer: 'Open the booking form and use the Repeat option within the Schedule section. Set the recurrence pattern and an end date or occurrence count.',
      },
      {
        question: 'Why can\'t I see a project in the booking form search?',
        answer: 'Projects that are inactive, archived, or that your account does not have permission to book against will not appear. Contact your planner administrator.',
      },
      {
        question: 'How do I delete a booking?',
        answer: 'Right-click the booking and choose "Delete" from the context menu, or click the booking to open the form and use the Delete button at the bottom. For recurring bookings, you can delete a single occurrence or all instances.',
      },
      {
        question: 'What does the right-click menu on an empty cell offer?',
        answer: 'Right-clicking an empty timeline cell gives you three options: "Add Booking" (opens the full booking form), "Quick Add Booking" (opens a compact fast-entry modal), and "Paste Booking" (only shown when a booking has been copied to the clipboard).',
      },
      {
        question: 'What does the right-click menu on a booking offer?',
        answer: 'Right-clicking a booking block gives you: Edit Booking, Copy Booking, Move, Change Status (with a submenu of all four statuses), and Delete Booking.',
      },
      {
        question: 'What is the difference between the grouping modes?',
        answer: 'Customer groups rows by client, showing all resources assigned to each customer. Project groups by project first, then resource underneath. Resource shows a flat alphabetical list of all resources. Role groups resources by their assigned role, useful for capacity planning by skill set.',
      },
      {
        question: 'What does the colour coding on bookings mean?',
        answer: 'Booking colours are determined by the booking type assigned in Settings → Booking Types. You can customise these colours to represent different categories of work.',
      },
      {
        question: 'How do I filter the planner?',
        answer: 'Click the Filter icon in the top-right toolbar to open the filter drawer. You can filter by department, resource, role, project, cost centre, booking type, and booking status. Active filters are shown with a count badge on the filter button.',
      },
    ],
    tourSteps: [
      {
        id: 'planner-toolbar',
        title: 'Toolbar & Navigation',
        description: 'The toolbar lets you navigate dates, switch between Day, Week, Month, Quarter, and Year views, and use zoom controls to adjust timeline density. The Add Booking button opens the booking form directly. The Filter icon (with an active-filter count badge) opens the filter panel where you can narrow the view by department, resource, role, project, cost centre, booking type, or status.',
        targetSelector: '[data-tour="planner-toolbar"]',
        placement: 'bottom',
        icon: 'CalendarDays',
      },
      {
        id: 'planner-grid',
        title: 'Timeline Grid',
        description: 'Each row is a resource, each column a date. Click an empty cell to open the booking form, or click and drag to pre-fill the date range. Right-click an empty cell for Add Booking, Quick Add, or Paste. The booking form slides in from the right with sections for Booking Type, Status, Schedule (Full Day, Morning, Afternoon, or Hourly — plus start/end dates and Repeat), Project/Customer, and Resources. Click or right-click any booking block to access Edit Booking, Copy Booking, Move, Change Status, or Delete Booking.',
        targetSelector: '[data-tour="planner-calendar"]',
        placement: 'bottom',
        icon: 'LayoutGrid',
      },
      {
        id: 'planner-grouping',
        title: 'Resource Grouping',
        description: 'The dropdown in the top-left of the resource column controls how rows are organised. Switch between Customer, Project, Resource, and Role grouping to get the view that best fits your planning workflow.',
        targetSelector: '[data-tour="planner-grouping"]',
        placement: 'right',
        icon: 'Layers',
      },
    ],
    relatedPages: [
      { label: 'Heatmap', page: 'heatmap' },
      { label: 'Open Demand', page: 'open-demand-unallocated' },
      { label: 'Resources', page: 'resources' },
    ],
  },

  // ─── Heatmap ────────────────────────────────────────────────────────────────
  heatmap: {
    pageTitle: 'Utilisation Heatmap',
    pageDescription: 'Visualise team capacity utilisation across time and quickly identify overloaded or under-utilised resources.',
    functionality: {
      overview:
        'The Heatmap provides a colour-coded bird\'s-eye view of how your team\'s capacity is being used. Each cell represents one resource for one time period. Colour intensity increases from green (under-utilised) through amber (approaching full) to red (overloaded), making bottlenecks immediately visible without drilling into individual calendars. In Month, Quarter, and Year views the heatmap also forecasts forward capacity by factoring in both Confirmed and Planned bookings, so you can see how committed work and pipeline demand combine against available capacity. The thresholds that define each colour band are configured in Settings → Company → Utilisation Bands, so the colours always reflect your organisation\'s own targets.',
      features: [
        {
          title: 'Colour-Coded Utilisation',
          description: 'Cell colours map to your configured utilisation bands — typically green for under-used, amber for near-capacity, and red for overloaded — so issues are visible at a glance. The percentage thresholds for each band are defined in Settings → Company → Utilisation Bands.',
        },
        {
          title: 'Heatmap Legend',
          description: 'A persistent legend bar sits below the grid and explains every visual indicator: the utilisation band colours and their percentage ranges, the ◈ symbol (cell contains only Planned bookings — no Confirmed work), the ◑ symbol (cell contains a mix of Confirmed and Planned bookings, with Planned making up more than half), and the ⚠ symbol (two or more bookings for that resource have overlapping dates — a hard scheduling conflict). The legend can be collapsed to save space and is restored on next visit.',
        },
        {
          title: 'Drill-Down View',
          description: 'Click any cell to see a breakdown of bookings for that resource and period, with project names and hours.',
        },
        {
          title: 'Burnout Risk Banner',
          description: 'When resources are trending toward sustained overload, an amber alert banner appears at the top with a count and a link to the What-if Analysis: Burnout Resolution panel.',
        },
        {
          title: 'What-if Analysis: Burnout Resolution',
          description: 'Opens an interactive overlay showing overloaded resources, recommended reassignments, and the projected impact of each option before committing.',
        },
        {
          title: 'Date Range Controls',
          description: 'Adjust the visible time window from a single week up to a full quarter to get the view you need.',
        },
        {
          title: 'Resource Group Filter',
          description: 'Filter the heatmap to show only specific teams, departments, or resource groups.',
        },
      ],
    },
    userGuide: {
      intro: 'Use the Heatmap to spot capacity issues early. The steps below guide you through the key workflows.',
      sections: [
        {
          title: 'How do I identify overloaded resources?',
          steps: [
            { step: 1, text: 'Look for red or deep-amber cells in the heatmap grid — these indicate over-allocation.' },
            { step: 2, text: 'Hover over a red cell to see the exact utilisation percentage and which bookings are causing the overload.' },
            { step: 3, text: 'Click the cell to open the drill-down view showing all bookings for that resource and period.' },
          ],
        },
        {
          title: 'How do I resolve an overallocation?',
          steps: [
            { step: 1, text: 'Click the amber "Burnout Risk" banner at the top of the page, or click any overloaded cell.' },
            { step: 2, text: 'The What-if Analysis: Burnout Resolution overlay opens, showing the overloaded resource and their current bookings.' },
            { step: 3, text: 'Review the recommended reassignment options. Each option shows the projected utilisation after the change.' },
            { step: 4, text: 'Select your preferred resolution and click Apply. The change is reflected in the planner immediately.' },
          ],
          note: 'Changes made in the What-if Analysis: Burnout Resolution panel are applied directly to bookings. You can undo immediately from the planner if the result is not what you expected.',
          noteType: 'info',
        },
      ],
    },
    faq: [
      {
        question: 'What do the different heatmap colours mean?',
        answer: 'Green cells indicate under-utilisation (below ~70%). Amber cells are approaching capacity (70–100%). Red cells are over-allocated (above 100%). The exact thresholds can be configured in Settings.',
      },
      {
        question: 'Why does a resource show as red when I know they have capacity?',
        answer: 'Overallocation is calculated from booked hours versus contracted hours. Check the resource\'s contracted weekly hours in the Resources page — they may be set lower than expected.',
      },
      {
        question: 'Can I adjust the utilisation thresholds for the colour bands?',
        answer: 'Yes. Navigate to Settings → General and look for the Utilisation Thresholds section to customise the amber and red breakpoints.',
      },
      {
        question: 'How far into the future does the heatmap show?',
        answer: 'The heatmap can display up to a 12-week rolling window. Use the date range controls at the top to move the window earlier or later.',
      },
      {
        question: 'What does the ⚠ symbol on a heatmap cell mean?',
        answer: 'The ⚠ warning triangle means two or more bookings for that resource have overlapping dates — a hard scheduling conflict where the same person is double-booked. This is distinct from overutilisation (being booked at more than 100%): a conflict means the booking dates literally overlap. Click the cell to see which bookings are clashing.',
      },
      {
        question: 'What do the ◈ and ◑ symbols mean on heatmap cells?',
        answer: '◈ means every booking in that cell is Planned — there is no Confirmed or Completed work in that period, so the utilisation figure is a forecast based on pipeline demand only. ◑ means the cell has a mix of Confirmed and Planned bookings, and Planned bookings make up more than half of the booked hours. These indicators help you distinguish committed capacity from forecast demand at a glance. Both symbols are explained in the legend bar below the grid.',
      },
      {
        question: 'How does the What-if Analysis: Burnout Resolution panel work?',
        answer: 'It matches the overloaded booking\'s required skills against available resources with spare capacity in the same period, then ranks candidates by availability score.',
      },
    ],
    tourSteps: [
      {
        id: 'heatmap-grid',
        title: 'Heatmap Grid',
        description: 'Each row is a resource, each column is a time period. Colour intensity shows utilisation — green is available, amber is near-full, red is overloaded. In Month, Quarter, and Year views, cells also show forecast demand from Planned bookings alongside Confirmed work. The ◈ symbol means a cell is fully Planned (no Confirmed work yet), ◑ means a mix with Planned in the majority, and ⚠ means two bookings overlap in date — a hard scheduling conflict. The legend bar below the grid explains every symbol and band.',
        targetSelector: '[data-tour="heatmap-grid"]',
        placement: 'bottom',
        icon: 'Activity',
      },
      {
        id: 'heatmap-burnout-banner',
        title: 'Burnout Risk Alert',
        description: 'This banner appears when one or more resources are trending toward sustained overload. Click it to jump straight into the What-if Analysis: Burnout Resolution panel.',
        targetSelector: '[data-tour="whatif-panel"]',
        placement: 'bottom',
        icon: 'AlertTriangle',
      },
    ],
    relatedPages: [
      { label: 'Planner', page: 'calendar' },
      { label: 'Utilisation Report', page: 'report-utilisation' },
    ],
  },

  // ─── Resources ──────────────────────────────────────────────────────────────
  resources: {
    pageTitle: 'Resources',
    pageDescription: 'View your team directory — availability, skills, utilisation, and current assignments for every resource.',
    functionality: {
      overview:
        'The Resources page is your read-only team directory, populated via the API from your Resource Manager application. It gives you a complete view of every resource — their role, department, skills, contracted hours, current utilisation rate, and upcoming bookings. Any changes to resource details, skills, or contracted hours must be made in Resource Manager; they will sync through to Scheduler automatically.',
      features: [
        {
          title: 'Resource Directory',
          description: 'A searchable, filterable table of all resources with their role, department, and current availability status. Data is fetched from Resource Manager via the API and is read-only within Scheduler.',
        },
        {
          title: 'Utilisation Rate',
          description: 'Each resource shows their current utilisation percentage based on booked hours versus contracted capacity as defined in Resource Manager.',
        },
        {
          title: 'Skills & Attributes',
          description: 'View the skills and custom attributes assigned to each resource in Resource Manager. These are used across the product to power resource suggestion features.',
        },
        {
          title: 'Filters & Search',
          description: 'Filter by department, role, availability, or skill to find the right person for a project quickly.',
        },
      ],
    },
    userGuide: {
      intro: 'The Resources page displays your team directory as synchronised from Resource Manager. All resource data is read-only in Scheduler.',
      sections: [
        {
          title: 'How do I find a resource?',
          steps: [
            { step: 1, text: 'Use the Search box at the top of the table to find a resource by name.' },
            { step: 2, text: 'Use the Department or Role filters to narrow down by team or job function.' },
            { step: 3, text: 'Click on a resource row to view their full profile and booking schedule.' },
          ],
        },
        {
          title: 'How do I update a resource\'s details or skills?',
          steps: [
            { step: 1, text: 'Open your Resource Manager application — resource data cannot be edited in Scheduler.' },
            { step: 2, text: 'Make the required changes to the resource\'s profile, role, department, contracted hours, or skills in Resource Manager.' },
            { step: 3, text: 'Changes will sync through to Scheduler automatically via the API.' },
          ],
          note: 'Resource data in Scheduler is read-only. All additions, edits, and deactivations must be made in the Resource Manager application.',
          noteType: 'warning',
        },
      ],
    },
    faq: [
      {
        question: 'How is the utilisation rate on the Resources page calculated?',
        answer: 'Utilisation is calculated as total booked hours divided by contracted weekly hours for the current rolling four-week period. Contracted hours are defined in Resource Manager.',
      },
      {
        question: 'Why can\'t I edit resource details in Scheduler?',
        answer: 'Resource data is managed centrally in your Resource Manager application and synchronised to Scheduler via the API. This ensures a single source of truth across all connected systems. Make any changes in Resource Manager and they will appear in Scheduler automatically.',
      },
      {
        question: 'Can a resource belong to multiple departments?',
        answer: 'Resources have one primary department as defined in Resource Manager, but can appear under different groups in the Planner depending on the grouping view selected.',
      },
      {
        question: 'What does "contracted hours" mean and why does it matter?',
        answer: 'Contracted hours define the maximum capacity of a resource per week, as set in Resource Manager. All utilisation percentages and overallocation warnings in Scheduler are based on this figure.',
      },
      {
        question: 'A resource is missing from the directory — what should I do?',
        answer: 'If a resource does not appear in Scheduler, check that they have been created and are active in Resource Manager. Once active in Resource Manager they will sync through to Scheduler via the API.',
      },
    ],
    tourSteps: [
      {
        id: 'resources-table',
        title: 'Resource Directory',
        description: 'Every row is a team member, synchronised from Resource Manager via the API. You can see their role, department, utilisation rate, and availability at a glance. Click a row to view their profile and booking schedule.',
        targetSelector: '[data-tour="resources-table"]',
        placement: 'bottom',
        icon: 'Users',
      },
      {
        id: 'resources-filters',
        title: 'Search & Filters',
        description: 'Use the search box and filter dropdowns to find resources by name, role, department, or skill set.',
        targetSelector: '[data-tour="resources-filters"]',
        placement: 'bottom',
        icon: 'Filter',
      },
    ],
    relatedPages: [
      { label: 'Planner', page: 'calendar' },
      { label: 'Resource Allocation Report', page: 'report-resource-allocation' },
    ],
  },

  // ─── Projects ───────────────────────────────────────────────────────────────
  projects: {
    pageTitle: 'Projects',
    pageDescription: 'Browse and review all projects that resources can be booked against.',
    functionality: {
      overview:
        'The Projects page is a read-only view of all projects synchronised into Scheduler from your PSA system via the API. Projects are created and managed in your PSA (e.g. Access Projects or FocalPoint) — any changes must be made there and will sync through automatically. In Scheduler you can browse projects, review their booking activity, and use them when creating bookings in the Planner.',
      features: [
        {
          title: 'Project Directory',
          description: 'A searchable table of all projects synchronised from your PSA system, showing status, owner, customer, and utilisation summary.',
        },
        {
          title: 'Project Detail',
          description: 'Click any project row to open its full detail view, which is organised into six tabs: Overview (key metrics, utilisation bars, start/end dates, tags), Team (all resources booked on the project with allocated vs actual hours and booking status), Bookings (planned, unresourced, and completed bookings grouped by status), Resource Budget (role-by-role budget vs actual hours and costs, synced from your PSA — only shown when live budget data is available), Cost Centres (cost centre breakdown including billable flag, status, and financial actuals where available), and Time (timesheet entries with billable/non-billable and approval status, synced from your PSA).',
        },
        {
          title: 'Status Filtering',
          description: 'Filter by Active, On Hold, or Archived to focus on the projects relevant to your current planning cycle.',
        },
        {
          title: 'Search',
          description: 'Search by project name, code, or client name to find a specific project quickly.',
        },
        {
          title: 'PSA Sync',
          description: 'Projects are synced automatically from your connected PSA system on a scheduled basis. The sync is configured in Settings → Integrations.',
        },
      ],
    },
    userGuide: {
      intro: 'The Projects page is read-only in Scheduler. Projects are managed in your PSA system and synced here via the API. Use this page to browse projects and review their scheduling activity.',
      sections: [
        {
          title: 'How do I explore a project\'s detail?',
          steps: [
            { step: 1, text: 'Find the project in the list using the search bar or status filters.' },
            { step: 2, text: 'Click the project row to open the detail panel.' },
            { step: 3, text: 'Use the Overview tab to see key metrics — start/end dates, budgeted hours, time elapsed, team size, and utilisation bars for hours and budget cost.' },
            { step: 4, text: 'Switch to the Team tab to see every resource booked on the project, their allocated vs actual hours, on-track status, and booking status (Confirmed, Planned, or No Booking).' },
            { step: 5, text: 'Switch to the Bookings tab to see all bookings grouped by status: Planned, Unresourced, and Completed. Unresourced bookings are highlighted and indicate demand that still needs a resource assigned.' },
            { step: 6, text: 'Switch to the Resource Budget tab (visible when live budget data is available from your PSA) to review role-by-role budgeted and actual hours and costs.' },
            { step: 7, text: 'Switch to the Cost Centres tab to see the cost centre breakdown for the project, including billable flag, status, and financial actuals where your PSA provides them.' },
            { step: 8, text: 'Switch to the Time tab to see timesheet entries with billable/non-billable split and approval status, synced from your PSA system.' },
          ],
        },
        {
          title: 'How do I update or add a project?',
          steps: [
            { step: 1, text: 'Log in to your PSA system (e.g. Access Projects or FocalPoint).' },
            { step: 2, text: 'Create or update the project there as required.' },
            { step: 3, text: 'The change will sync through to Scheduler automatically on the next scheduled sync, or you can trigger a manual sync in Settings → Integrations.' },
          ],
          note: 'Projects cannot be created or edited directly in Scheduler. All project management is handled in your PSA system.',
          noteType: 'warning',
        },
      ],
    },
    faq: [
      {
        question: 'Why doesn\'t a project appear in the Planner booking form?',
        answer: 'Only Active projects appear in the booking form search. If a project is missing, check its status in your PSA system and ensure it has synced through to Scheduler via Settings → Integrations.',
      },
      {
        question: 'Why can\'t I see the Resource Budget tab on a project?',
        answer: 'The Resource Budget tab is only shown when live budget data is available from your PSA system. If the tab is not visible, the project either has no resource budget configured in your PSA, or the integration has not synced that data yet.',
      },
      {
        question: 'What does "Unresourced" mean in the Bookings tab?',
        answer: 'An unresourced booking is one that has been created for the project but has no resource assigned to it. These represent open demand that still needs allocating. You can use the Open Demand page to action these.',
      },
      {
        question: 'Can I edit a project\'s details in Scheduler?',
        answer: 'No. Projects are read-only in Scheduler. All changes to project details, status, ownership, or budget must be made in your PSA system and will sync through automatically.',
      },
      {
        question: 'How does PSA sync work?',
        answer: 'When a PSA integration is configured in Settings → Integrations, projects are synced on a scheduled basis. You can also trigger a manual sync from the Integrations settings page.',
      },
      {
        question: 'A project is missing — what should I do?',
        answer: 'Check that the project exists and is active in your PSA system. If it is, go to Settings → Integrations and trigger a manual sync. If it still does not appear, contact your system administrator to check the integration configuration.',
      },
    ],
    tourSteps: [
      {
        id: 'projects-list',
        title: 'Project List',
        description: 'Every row is a project synchronised from your PSA system via the API. Projects are read-only in Scheduler — create or update them in your PSA and they will sync through automatically. Click a row to open the project detail view.',
        targetSelector: '[data-tour="projects-list"]',
        placement: 'bottom',
        icon: 'FolderKanban',
      },
    ],
    relatedPages: [
      { label: 'Planner', page: 'calendar' },
      { label: 'Settings', page: 'settings' },
    ],
  },

  // ─── Settings ───────────────────────────────────────────────────────────────
  settings: {
    pageTitle: 'Settings',
    pageDescription: 'Configure booking types, working hours, user permissions, integrations, and planner behaviour for your organisation.',
    functionality: {
      overview:
        'Settings is the administration hub for Scheduler. Here administrators can define booking types and their visual styles, configure contracted working hours, manage user roles and permissions, and set up integrations with external PSA systems. Personal preferences such as notifications and display options are also managed here.',
      features: [
        {
          title: 'Booking Types',
          description: 'Create and manage the booking categories used throughout the planner — such as Billable, Internal, Leave, or Training — with custom colours and field configurations.',
        },
        {
          title: 'Working Hours',
          description: 'Define standard working days and hours for your organisation, used as the baseline for utilisation calculations and capacity planning.',
        },
        {
          title: 'Users & Roles',
          description: 'Invite team members, assign roles (Admin, Planner, Viewer), and manage access permissions per resource group.',
        },
        {
          title: 'Integrations',
          description: 'Connect Scheduler to your PSA system (Access Projects or FocalPoint) to automatically sync projects and resources.',
        },
        {
          title: 'Planner Groups',
          description: 'Organise resources into logical groups for filtering and display in the planner timeline.',
        },
        {
          title: 'Notifications',
          description: 'Configure email notification rules for booking approvals, rejections, and capacity warnings.',
        },
        {
          title: 'Custom Fields',
          description: 'Add custom data fields to the booking form that capture information specific to your team\'s workflows.',
        },
      ],
    },
    userGuide: {
      intro: 'Use Settings to tailor Scheduler to your organisation\'s processes and preferences.',
      sections: [
        {
          title: 'How do I create a new booking type?',
          steps: [
            { step: 1, text: 'Navigate to the Booking Types tab in Settings.' },
            { step: 2, text: 'Click Add Booking Type and enter a name and description.' },
            { step: 3, text: 'Choose a colour — this will be used to display bookings of this type in the planner.' },
            { step: 4, text: 'Configure any custom fields or options specific to this booking type.' },
            { step: 5, text: 'Click Save. The booking type is immediately available in the Planner booking form.' },
          ],
        },
        {
          title: 'How do I connect a PSA integration?',
          steps: [
            { step: 1, text: 'Go to the Integrations tab in Settings.' },
            { step: 2, text: 'Select your PSA system from the list (Access Projects, FocalPoint, etc.).' },
            { step: 3, text: 'Enter your API credentials as directed by the on-screen guide.' },
            { step: 4, text: 'Click Test Connection to verify the credentials are correct.' },
            { step: 5, text: 'Click Save & Sync to import projects and resources from your PSA.' },
          ],
          note: 'You will need API credentials from your PSA system. Contact your PSA administrator if you do not have these.',
          noteType: 'info',
        },
      ],
    },
    faq: [
      {
        question: 'Who can access the Settings page?',
        answer: 'Only users with the Admin role can access and modify Settings. Planner and Viewer roles cannot see this page.',
      },
      {
        question: 'Will changing working hours affect existing bookings?',
        answer: 'Changing working hours updates future utilisation calculations but does not retroactively alter existing booking data.',
      },
      {
        question: 'Can I create different booking types for different teams?',
        answer: 'Yes. Booking types can be restricted to specific planner groups, so only the relevant teams see them in the booking form.',
      },
      {
        question: 'How often does the PSA sync run automatically?',
        answer: 'The default sync frequency is every 4 hours. You can change this in the Integrations tab, or trigger a manual sync at any time.',
      },
      {
        question: 'What happens if I delete a booking type that has existing bookings?',
        answer: 'You cannot delete a booking type that has active bookings. Archive it instead — this hides it from new bookings while preserving historical data.',
      },
    ],
    tourSteps: [
      {
        id: 'settings-tabs',
        title: 'Settings Tabs',
        description: 'Navigate between General, Booking Types, Working Hours, Users, Integrations, and more using the tab bar at the top of the page.',
        targetSelector: '[data-tour="settings"]',
        placement: 'bottom',
        icon: 'Settings',
      },
    ],
    relatedPages: [
      { label: 'Resources', page: 'resources' },
      { label: 'Planner', page: 'calendar' },
    ],
  },

  // ─── Open Demand → Resource Requests ────────────────────────────────────────
  'open-demand-requests': {
    pageTitle: 'Resource Requests',
    pageDescription: 'Review and plan inbound resource requests raised in Access Projects.',
    functionality: {
      overview:
        'The Resource Requests page surfaces inbound demand from Access Projects — each request represents a role requirement raised against a project and arrives with priority, hours, and start date information. Plan a request to generate suggested resourcing scenarios, preview them on the calendar, and accept the best fit to create bookings.',
      features: [
        {
          title: 'Resource Requests (Access Projects)',
          description: 'Inbound requests from Access Projects are listed with priority (Critical, High, Medium, Low), required role, total hours, start date, and current status (Pending, Plan Ready, Accepted, Declined). Expand any row to see full request detail including monthly hour distribution and budget context.',
        },
        {
          title: 'Resource Request Planning',
          description: 'Each resource request can be planned using the Plan Drawer, which generates suggested booking scenarios across available resources. Scenarios can be previewed on the calendar before being accepted. Matching is based on role, skills, and available capacity.',
        },
        {
          title: 'Request Statuses',
          description: 'Requests move through four statuses: Pending (awaiting a plan), Plan Ready (a scenario has been generated and is ready to review), Accepted (resourcing confirmed), and Declined (request rejected).',
        },
        {
          title: 'Filters & Search',
          description: 'Filter resource requests by status and priority, and search by project name, customer, or role to find the demand you need to action.',
        },
        {
          title: 'Column Configuration',
          description: 'The Resource Requests table supports configurable columns — show, hide, and reorder columns to suit your workflow. Preferences are saved per user.',
        },
      ],
    },
    userGuide: {
      intro: 'Use Resource Requests to action inbound demand from Access Projects before any bookings exist.',
      sections: [
        {
          title: 'How do I action a resource request from Access Projects?',
          steps: [
            { step: 1, text: 'Find the request in the list — requests are sorted by priority by default.' },
            { step: 2, text: 'Click the row to expand it and review the full detail — role, hours per month, start date, and budget context.' },
            { step: 3, text: 'Click Plan to open the Resource Request Plan Drawer. The system generates suggested booking scenarios across available resources.' },
            { step: 4, text: 'Review the scenarios, preview them on the calendar if needed, then Accept the best-fit plan.' },
            { step: 5, text: 'The request status updates to Accepted and the bookings are created in the Planner.' },
          ],
          note: 'Resource requests are read from Access Projects via the configured integration. If no requests appear, check the integration settings under Settings → Integrations.',
          noteType: 'info',
        },
      ],
    },
    faq: [
      {
        question: 'Where do resource requests come from?',
        answer: 'Resource requests are inbound demand from Access Projects, pulled in via the configured PSA integration. Each request corresponds to a role requirement raised against a project in Access Projects.',
      },
      {
        question: 'What do the request statuses mean?',
        answer: 'Pending — no plan created yet. Plan Ready — a resourcing scenario has been generated and is awaiting review. Accepted — the plan has been confirmed and bookings created. Declined — the request has been rejected.',
      },
      {
        question: 'Can I preview a resource plan before accepting it?',
        answer: 'Yes. In the Resource Request Plan Drawer, use the Preview on Calendar button on any scenario to see how the proposed bookings would appear on the planner before committing.',
      },
    ],
    tourSteps: [
      {
        id: 'resource-requests-section',
        title: 'Resource Requests',
        description: 'Inbound demand from Access Projects appears here. Each request shows the required role, hours, priority, and current status. Expand a row to see full detail, then click Plan to generate suggested resourcing scenarios.',
        targetSelector: '[data-tour="resource-requests-section"]',
        placement: 'right',
        icon: 'Package',
      },
    ],
    relatedPages: [
      { label: 'Unallocated Bookings', page: 'open-demand-unallocated' },
      { label: 'Planner', page: 'calendar' },
      { label: 'Settings', page: 'settings' },
    ],
  },

  // ─── Open Demand → Unallocated Bookings ─────────────────────────────────────
  'open-demand-unallocated': {
    pageTitle: 'Unallocated Bookings',
    pageDescription: 'Allocate bookings that exist in the scheduler but have no resource assigned yet.',
    functionality: {
      overview:
        'The Unallocated Bookings page lists bookings that exist in the scheduler but have no resource assigned yet, colour-coded by urgency. Filter and sort the list, then use smart suggestions — individually or in batch — to allocate work to the right people.',
      features: [
        {
          title: 'Unallocated Bookings',
          description: 'Bookings created in the Planner without a resource assigned appear here, colour-coded by urgency — Critical (starting within 3 days), Soon (within 7 days), and Upcoming (everything else). Each row shows project, customer, date range, required role, skills, and location.',
        },
        {
          title: 'Smart Resource Suggestion',
          description: 'Click Suggest Resource on any unallocated booking to open the resource recommendation panel. It filters available resources by required skills and ranks them by spare capacity in the booking period. Select a resource and confirm to assign the booking.',
        },
        {
          title: 'Batch Suggest',
          description: 'Select multiple unallocated bookings and use Suggest Resources — All to generate assignments for all selected bookings at once, saving time when dealing with large volumes of open demand.',
        },
        {
          title: 'View in Planner',
          description: 'Jump directly from any unallocated booking to the Planner with that booking in context, so you can manually drag it onto a resource row yourself.',
        },
        {
          title: 'Filters & Search',
          description: 'Filter unallocated bookings by urgency, project, booking type, required role, or skills, and search by project name, customer, or role.',
        },
        {
          title: 'Column Configuration',
          description: 'The Unallocated Bookings table supports configurable columns — show, hide, and reorder columns to suit your workflow. Preferences are saved per user.',
        },
        {
          title: 'Export to Excel',
          description: 'Export the current filtered view of unallocated bookings to an Excel spreadsheet for offline review or reporting.',
        },
      ],
    },
    userGuide: {
      intro: 'Use Unallocated Bookings to stay on top of bookings waiting for a resource assignment.',
      sections: [
        {
          title: 'How do I allocate an unallocated booking?',
          steps: [
            { step: 1, text: 'Find the booking in the list — use urgency filters or search to locate it quickly.' },
            { step: 2, text: 'Click Suggest Resource to open the smart allocation panel.' },
            { step: 3, text: 'Review the recommended resources ranked by availability and skill match.' },
            { step: 4, text: 'Select your preferred resource and confirm. The booking is assigned and moves off the Open Demand list.' },
          ],
          note: 'If no resource is suggested, the booking may specify skills or a role that no available resource holds. Check resource profiles in the Resources page.',
          noteType: 'warning',
        },
        {
          title: 'How do I allocate multiple unallocated bookings at once?',
          steps: [
            { step: 1, text: 'Select multiple unallocated bookings using the checkboxes on each row.' },
            { step: 2, text: 'Click Suggest Resources — All in the toolbar.' },
            { step: 3, text: 'Review the suggested assignments for all selected bookings.' },
            { step: 4, text: 'Confirm to apply all assignments at once.' },
          ],
        },
        {
          title: 'How do I view an unallocated booking in the Planner?',
          steps: [
            { step: 1, text: 'Click View in Planner on the booking row.' },
            { step: 2, text: 'The Planner opens with the booking in context.' },
            { step: 3, text: 'Drag the booking onto the resource row of your choice to allocate it manually.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'Where do unallocated bookings come from?',
        answer: 'Unallocated bookings are created in the Planner when a booking is saved without a resource assigned — for example, when demand is known but resourcing is not yet decided.',
      },
      {
        question: 'What do the urgency levels mean on unallocated bookings?',
        answer: 'Critical — the booking starts within 3 days. Soon — starts within 7 days. Upcoming — starts further in the future. These help you prioritise which unallocated work needs attention first.',
      },
      {
        question: 'How does the resource suggestion work?',
        answer: 'Resources are filtered by the required role and skills on the booking, then ranked by available capacity in the booking\'s date range. The best-fit resource is shown first.',
      },
    ],
    tourSteps: [
      {
        id: 'unallocated-list',
        title: 'Unallocated Bookings',
        description: 'Bookings without a resource assigned are listed here, colour-coded by urgency. Use Suggest Resource for recommended matches, Suggest Resources — All to action multiple bookings at once, or View in Planner to allocate manually on the timeline.',
        targetSelector: '[data-tour="unallocated-list"]',
        placement: 'right',
        icon: 'Inbox',
      },
    ],
    relatedPages: [
      { label: 'Resource Requests', page: 'open-demand-requests' },
      { label: 'Planner', page: 'calendar' },
      { label: 'Resources', page: 'resources' },
    ],
  },

  // ─── Overview ───────────────────────────────────────────────────────────────
  overview: {
    pageTitle: 'Dashboard Overview',
    pageDescription: 'A high-level summary of team utilisation, open demand, and recent scheduling activity.',
    functionality: {
      overview:
        'The Dashboard Overview provides a snapshot of your team\'s current state. KPI cards show total resources, open demand count, average utilisation, and overloaded resources at a glance. Charts give trend data to help you spot patterns and take action early.',
      features: [
        {
          title: 'KPI Cards',
          description: 'At-a-glance metrics for total resources, current open demand, average team utilisation, and number of overloaded resources.',
        },
        {
          title: 'Utilisation Chart',
          description: 'A bar or line chart showing utilisation trends over the past 4–8 weeks to spot patterns early.',
        },
        {
          title: 'Open Demand Summary',
          description: 'A count and breakdown of unallocated bookings by urgency, with a direct link to the Open Demand page.',
        },

      ],
    },
    userGuide: {
      intro: 'The Overview gives you a daily health check of your team\'s scheduling status.',
      sections: [
        {
          title: 'How do I use the Overview dashboard?',
          steps: [
            { step: 1, text: 'Review the KPI cards at the top — these give you an instant summary of the team\'s status.' },
            { step: 2, text: 'Check the utilisation trend chart to see if the team is trending up or down in workload.' },
            { step: 3, text: 'If open demand is high, click the Open Demand summary to go directly to the allocation page.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'How often does the Overview data refresh?',
        answer: 'Overview data refreshes automatically every time you navigate to the page. You can also force a refresh using your browser\'s reload button.',
      },
      {
        question: 'Can I customise which KPIs appear on the Overview?',
        answer: 'Custom KPI configuration is on the roadmap. Currently the displayed metrics are fixed but reflect the most commonly needed planning indicators.',
      },
    ],
    tourSteps: [
      {
        id: 'overview-kpis',
        title: 'Dashboard Overview',
        description: 'The Dashboard Overview is your morning snapshot — a single screen that tells you the health of your team before you open the planner. The five KPI cards across the top show Total Resources, Team Utilisation, Open Demand, Burnout Risk, and Budget Health, each with a live tooltip explaining how the metric is calculated. Secondary cards below surface Forecast Utilisation, Available Capacity, Bench Resources, and Coming Free — giving you a forward-looking view alongside today\'s position.',
        targetSelector: '[data-tour="overview-kpis"]',
        placement: 'bottom',
        icon: 'BarChart2',
      },
      {
        id: 'overview-trend',
        title: 'Utilisation Trend & Alerts',
        description: 'The Utilisation Trend chart shows your team\'s average weekly utilisation over the last eight weeks, colour-coded against the target thresholds configured in Settings. Below the KPIs, contextual alert banners appear when action is needed — a Burnout Risk alert with a direct link to Heatmap resolution, and an Upcoming Availability notice when resources are coming free so you can plan ahead and avoid bench time.',
        targetSelector: '[data-tour="overview-trend"]',
        placement: 'top',
        icon: 'TrendingUp',
      },
    ],
    relatedPages: [
      { label: 'Planner', page: 'calendar' },
      { label: 'Heatmap', page: 'heatmap' },
    ],
  },

  // ─── Utilisation Report ─────────────────────────────────────────────────────
  'report-utilisation': {
    pageTitle: 'Utilisation Report',
    pageDescription: 'Review historical and current utilisation rates across resources, groups, and projects.',
    functionality: {
      overview:
        'The Utilisation Report provides a detailed breakdown of how team capacity has been used across a selected date range. You can view utilisation by individual resource, by planner group, or rolled up to organisation level. The report is used for billing reviews, performance tracking, and capacity trend analysis.',
      features: [
        {
          title: 'Date Range Selector',
          description: 'Choose any historical or future date range for the report. Pre-sets include This Week, Last Month, Last Quarter, and custom ranges.',
        },
        {
          title: 'Resource & Group Filters',
          description: 'Narrow the report to specific individuals, teams, or booking types.',
        },
        {
          title: 'Billable vs Non-Billable',
          description: 'Split utilisation into billable and non-billable categories based on booking type configuration.',
        },
        {
          title: 'CSV Export',
          description: 'Download the report data as a CSV for use in external reporting tools or spreadsheets.',
        },
        {
          title: 'Heatmap Link',
          description: 'Click any resource row to jump directly to the heatmap filtered to that resource for deeper capacity analysis.',
        },
      ],
    },
    userGuide: {
      intro: 'Use this report to review team utilisation over any time period.',
      sections: [
        {
          title: 'How do I run the Utilisation Report?',
          steps: [
            { step: 1, text: 'Set the date range using the date picker at the top of the page.' },
            { step: 2, text: 'Optionally apply resource group or booking type filters.' },
            { step: 3, text: 'The report table updates automatically with utilisation percentages and hours.' },
            { step: 4, text: 'Click Export to download the results as CSV.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'What is the difference between utilisation and capacity?',
        answer: 'Capacity is the total available hours (contracted hours × working days). Utilisation is the percentage of that capacity that has been booked.',
      },
      {
        question: 'Can I see billable utilisation separately?',
        answer: 'Yes. Use the Booking Type filter to include only billable booking types, or enable the Billable/Non-Billable split toggle.',
      },
    ],
    tourSteps: [
      {
        id: 'report-util-table',
        title: 'Utilisation Table',
        description: 'Each row shows a resource\'s utilisation percentage, total hours booked, and available capacity for the selected date range.',
        targetSelector: '[data-tour="reports-suite"]',
        placement: 'bottom',
        icon: 'BarChart2',
      },
    ],
    relatedPages: [
      { label: 'Heatmap', page: 'heatmap' },
      { label: 'Utilisation Forecast', page: 'report-utilisation-forecast' },
    ],
  },

  // ─── Utilisation Forecast ───────────────────────────────────────────────────
  'report-utilisation-forecast': {
    pageTitle: 'Utilisation Forecast',
    pageDescription: 'Project future utilisation based on confirmed bookings to identify capacity gaps before they become problems.',
    functionality: {
      overview:
        'The Utilisation Forecast report projects how your team\'s capacity will be used over the coming weeks and months, based on current confirmed bookings. It helps you identify future over-allocation and under-utilisation early, so you can adjust the schedule or resources before it becomes a problem.',
      features: [
        {
          title: 'Forward-Looking Timeline',
          description: 'View projected utilisation week-by-week for the next 4 to 26 weeks.',
        },
        {
          title: 'Capacity Gap Detection',
          description: 'Periods where utilisation exceeds 100% or drops below a configurable minimum are highlighted automatically.',
        },
        {
          title: 'Resource & Group Breakdown',
          description: 'Drill down from team-level forecast to individual resource forecasts.',
        },
        {
          title: 'CSV Export',
          description: 'Export forecast data for capacity planning meetings or external reporting.',
        },
      ],
    },
    userGuide: {
      intro: 'Use the Forecast report to plan ahead and prevent capacity crises.',
      sections: [
        {
          title: 'How do I read the forecast?',
          steps: [
            { step: 1, text: 'Select the forecast window — choose from 4, 8, 13, or 26 weeks ahead.' },
            { step: 2, text: 'Look for red-highlighted weeks where team utilisation exceeds 100%.' },
            { step: 3, text: 'Click any highlighted week to drill down to the individual resource level.' },
            { step: 4, text: 'Use the Planner to add or remove bookings to balance the forecast.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'How far ahead does the forecast go?',
        answer: 'The forecast shows up to 26 weeks ahead based on confirmed bookings. It does not include tentative or draft bookings unless that filter is enabled.',
      },
      {
        question: 'Does the forecast update when I add new bookings?',
        answer: 'Yes. The forecast is recalculated in real time whenever a booking is added, changed, or deleted.',
      },
    ],
    tourSteps: [],
    relatedPages: [
      { label: 'Utilisation Report', page: 'report-utilisation' },
      { label: 'Heatmap', page: 'heatmap' },
    ],
  },

  // ─── Available Capacity ─────────────────────────────────────────────────────
  'report-available-capacity': {
    pageTitle: 'Available Capacity',
    pageDescription: 'Find resources with available hours in a specific period to fulfil incoming project demand.',
    functionality: {
      overview:
        'The Available Capacity report helps you quickly find who has spare capacity within a given date range. This is typically used when a new project needs to be staffed — enter the required hours and skills, and the report returns a ranked list of resources who can take the work.',
      features: [
        {
          title: 'Date Range Search',
          description: 'Specify the period you need coverage for and the report calculates remaining capacity per resource.',
        },
        {
          title: 'Hours Filter',
          description: 'Filter to show only resources with at least a specified number of available hours in the period.',
        },
        {
          title: 'Skills Filter',
          description: 'Narrow results to resources with specific skills required for the project.',
        },
        {
          title: 'Book Directly',
          description: 'Click any resource in the results to open the Planner booking form pre-populated with their details and the selected date range.',
        },
      ],
    },
    userGuide: {
      intro: 'Use this report to staff new projects quickly by finding who has the right capacity.',
      sections: [
        {
          title: 'How do I find available resources?',
          steps: [
            { step: 1, text: 'Set the date range for the period you need to staff.' },
            { step: 2, text: 'Enter the minimum hours required.' },
            { step: 3, text: 'Optionally add a skills filter to match project requirements.' },
            { step: 4, text: 'Click Search. The table shows resources ranked by available hours.' },
            { step: 5, text: 'Click Book next to any resource to create a booking directly.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'Does Available Capacity include part-time workers?',
        answer: 'Yes. Capacity is calculated from each resource\'s individual contracted hours, so part-time resources show proportionally lower available hours.',
      },
    ],
    tourSteps: [],
    relatedPages: [
      { label: 'Resources', page: 'resources' },
      { label: 'Planner', page: 'calendar' },
    ],
  },

  // ─── Resource Allocation ────────────────────────────────────────────────────
  'report-resource-allocation': {
    pageTitle: 'Resource Allocation',
    pageDescription: 'Review how each resource\'s time is allocated across projects and booking types for a given period.',
    functionality: {
      overview:
        'The Resource Allocation report shows a detailed breakdown of how each resource has spent their time, split by project and booking type. It is used to review whether resources are spending their time aligned to business priorities and to generate data for invoicing or cost allocation.',
      features: [
        {
          title: 'Per-Resource Breakdown',
          description: 'See hours and percentages split by project and booking type for each resource.',
        },
        {
          title: 'Time Period Filters',
          description: 'Filter by week, month, quarter, or custom date range.',
        },
        {
          title: 'Project & Group Filters',
          description: 'Narrow to a specific project, client, or resource group.',
        },
        {
          title: 'CSV Export',
          description: 'Export for use in payroll, invoicing, or management reporting.',
        },
      ],
    },
    userGuide: {
      intro: 'Use this report for billing, time tracking, and resource performance reviews.',
      sections: [
        {
          title: 'How do I run the Resource Allocation Report?',
          steps: [
            { step: 1, text: 'Select the date range you want to report on.' },
            { step: 2, text: 'Optionally filter by resource group or project.' },
            { step: 3, text: 'Review the allocation table. Each row breaks down one resource\'s time by project.' },
            { step: 4, text: 'Click Export to download the data as CSV.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'Can I filter this report to just billable hours?',
        answer: 'Yes. Use the Booking Type filter and select only the booking types classified as billable.',
      },
    ],
    tourSteps: [],
    relatedPages: [
      { label: 'Utilisation Report', page: 'report-utilisation' },
      { label: 'Resources', page: 'resources' },
    ],
  },

  // ─── Project Staffing ───────────────────────────────────────────────────────
  'report-project-staffing': {
    pageTitle: 'Project Staffing',
    pageDescription: 'Review staffing levels and hours allocation across individual projects.',
    functionality: {
      overview:
        'The Project Staffing report focuses on the project dimension — showing how many resources are assigned to each project, how many hours have been booked, and how that compares to the planned budget. It is the primary tool for project managers to track whether projects are on track from a staffing perspective.',
      features: [
        {
          title: 'Project-by-Project View',
          description: 'Each project is a row showing resource count, total booked hours, and percentage of budget consumed.',
        },
        {
          title: 'Budget vs Actuals',
          description: 'Compare booked hours to the project budget target to spot over-servicing or under-servicing early.',
        },
        {
          title: 'Resource Drill-Down',
          description: 'Click any project to see the breakdown by individual resource, showing hours by person.',
        },
        {
          title: 'Navigate to Heatmap',
          description: 'Click View in Heatmap to jump directly to the heatmap filtered to that project.',
        },
      ],
    },
    userGuide: {
      intro: 'Use this report to keep project staffing levels aligned with delivery plans.',
      sections: [
        {
          title: 'How do I check if a project is over-resourced?',
          steps: [
            { step: 1, text: 'Find the project in the list using search or filters.' },
            { step: 2, text: 'Check the Budget vs Actuals column — a figure above 100% means booked hours exceed the planned budget.' },
            { step: 3, text: 'Click the project row to see the breakdown by resource.' },
            { step: 4, text: 'Navigate to the Planner to remove or reduce bookings as needed.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'Where does the project budget figure come from?',
        answer: 'Budget hours are set on the project detail page. If no budget is set, the Budget vs Actuals column will show "No budget" for that project.',
      },
      {
        question: 'Can I see project staffing for future periods?',
        answer: 'Yes. Set the date range to a future period to see planned staffing based on current confirmed bookings.',
      },
    ],
    tourSteps: [],
    relatedPages: [
      { label: 'Projects', page: 'projects' },
      { label: 'Available Capacity', page: 'report-available-capacity' },
    ],
  },

  // ─── Bench Report ───────────────────────────────────────────────────────────
  'report-bench': {
    pageTitle: 'Bench Report',
    pageDescription: 'Identify resources with little or no bookings in an upcoming period — those currently "on the bench".',
    functionality: {
      overview:
        'The Bench Report identifies resources who have zero or minimal bookings in a specified upcoming period. It helps resource managers proactively find under-utilised team members who could be assigned to new projects or have their time re-planned before the period begins.',
      features: [
        {
          title: 'Bench Detection',
          description: 'Automatically flags resources with utilisation below a configurable threshold for the selected period.',
        },
        {
          title: 'Date Range Controls',
          description: 'Select any upcoming period to check who is available and under-booked.',
        },
        {
          title: 'Threshold Filter',
          description: 'Set a custom utilisation threshold — e.g. show all resources below 50% booked.',
        },
        {
          title: 'Quick Actions',
          description: 'Click any benched resource to jump to their availability in the Available Capacity report or open the Planner.',
        },
      ],
    },
    userGuide: {
      intro: 'Use the Bench Report to find resources who need new work assigned before they become under-utilised.',
      sections: [
        {
          title: 'How do I find under-utilised resources?',
          steps: [
            { step: 1, text: 'Set the date range to the upcoming period you want to review (e.g. next 2 weeks).' },
            { step: 2, text: 'Adjust the bench threshold if needed (default is resources below 50% utilised).' },
            { step: 3, text: 'Review the list of benched resources, sorted by lowest utilisation first.' },
            { step: 4, text: 'Click a resource to view their availability and assign new bookings.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'What counts as "on the bench"?',
        answer: 'A resource is considered benched when their projected utilisation for the selected period falls below the configured threshold (default: 50%).',
      },
      {
        question: 'How do I change the bench threshold?',
        answer: 'Use the Threshold slider or input at the top of the report page to adjust the percentage cutoff.',
      },
    ],
    tourSteps: [],
    relatedPages: [
      { label: 'Available Capacity', page: 'report-available-capacity' },
      { label: 'Resources', page: 'resources' },
    ],
  },

  // ─── Roles & Permissions ───────────────────────────────────────────────────
  'roles-permissions': {
    pageTitle: 'Roles & Permissions',
    pageDescription: 'Define user roles and configure what each role can see and do within Scheduler.',
    functionality: {
      overview:
        'The Roles & Permissions page lets administrators define granular access control for the application. Each role is a named collection of permissions — controlling which pages, actions, and data a user of that role can access. Changes take effect immediately when a user\'s role is updated.',
      features: [
        {
          title: 'Role Management',
          description: 'Create custom roles or edit the default Admin, Planner, and Viewer roles to match your organisation\'s access requirements.',
        },
        {
          title: 'Permission Matrix',
          description: 'A clear table of all available permissions, showing which are enabled or disabled for each role.',
        },
        {
          title: 'Assign Roles',
          description: 'Assign roles to users from the Users section of Settings or directly from this page.',
        },
      ],
    },
    userGuide: {
      intro: 'Use this page to control who can do what in Scheduler.',
      sections: [
        {
          title: 'How do I create a custom role?',
          steps: [
            { step: 1, text: 'Click Create Role at the top of the page.' },
            { step: 2, text: 'Give the role a name and description.' },
            { step: 3, text: 'Toggle each permission on or off in the permission matrix.' },
            { step: 4, text: 'Click Save Role. The role is now available to assign to users.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'Can I edit the built-in Admin role?',
        answer: 'The Admin role has all permissions and cannot be restricted. You can create a custom role with a subset of admin permissions if needed.',
      },
    ],
    tourSteps: [],
    relatedPages: [
      { label: 'Settings', page: 'settings' },
    ],
  },

  // ─── User Setup ─────────────────────────────────────────────────────────────
  'user-setup': {
    pageTitle: 'User Setup',
    pageDescription: 'Complete your personal profile and preferences for Scheduler.',
    functionality: {
      overview:
        'User Setup lets you configure your personal profile within Scheduler — including your display name, preferred timezone, notification preferences, and default planner view. This page is typically shown on first login but can be revisited from the profile menu at any time.',
      features: [
        {
          title: 'Profile Information',
          description: 'Update your display name, email, and profile avatar.',
        },
        {
          title: 'Timezone',
          description: 'Set your local timezone so booking times are displayed correctly for your location.',
        },
        {
          title: 'Default View',
          description: 'Choose your preferred default planner view — Daily, Weekly, or Monthly — so the planner always opens the way you like it.',
        },
        {
          title: 'Notification Preferences',
          description: 'Configure which email notifications you receive for booking events such as approvals, rejections, and reminders.',
        },
      ],
    },
    userGuide: {
      intro: 'Complete your setup to personalise your Scheduler experience.',
      sections: [
        {
          title: 'How do I complete my user setup?',
          steps: [
            { step: 1, text: 'Review your profile information and update your display name if needed.' },
            { step: 2, text: 'Select your timezone from the dropdown.' },
            { step: 3, text: 'Choose your preferred default planner view.' },
            { step: 4, text: 'Configure your notification preferences.' },
            { step: 5, text: 'Click Save to apply your settings.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'Can I change my timezone after initial setup?',
        answer: 'Yes. Return to this page from the profile menu in the top navigation and update your timezone at any time.',
      },
    ],
    tourSteps: [],
    relatedPages: [
      { label: 'Settings', page: 'settings' },
    ],
  },
};

// Helper to get content for the current page, falling back to calendar
export function getHelpContent(page: string): PageHelpContent {
  return HELP_CONTENT[page as HelpPageKey] ?? HELP_CONTENT['calendar'];
}
